import { useState, useEffect, useRef } from 'react'
import RoleSelection from './components/RoleSelection'
import DialingScreen from './components/DialingScreen'
import CallInterface from './components/CallInterface'
import EndCallScreen from './components/EndCallScreen'
import { Role } from './types'

export type AppState = 'role-selection' | 'calling' | 'in-call' | 'ended'

function App() {
  const [appState, setAppState] = useState<AppState>('role-selection')
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isWebsiteMode, setIsWebsiteMode] = useState<boolean>(false)
  const backendWsRef = useRef<WebSocket | null>(null)

  // Clear any existing session data on app startup and detect query parameters
  useEffect(() => {
    // Always clear session data when app starts (fresh load or page refresh)
    localStorage.removeItem('talkToZayn_sessionId')
    localStorage.removeItem('talkToZayn_role')
    console.log('Cleared any existing session data on app startup')
    
    // Check for website query parameter
    const urlParams = new URLSearchParams(window.location.search)
    const hasWebsiteParam = urlParams.has('website')
    setIsWebsiteMode(hasWebsiteParam)
    console.log('Website mode detected:', hasWebsiteParam)
  }, [])

  const handleRoleSelect = (role: Role) => {
    // Reset global call ended flag
    ;(window as any).callEnded = false
    // Reset greeting audio played flag
    ;(window as any).hasPlayedGreetingAudio = false
    
    // Generate session ID when role is selected
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)
    
    // Store in localStorage for persistence
    localStorage.setItem('talkToZayn_sessionId', newSessionId)
    localStorage.setItem('talkToZayn_role', JSON.stringify(role))
    
    setSelectedRole(role)
    setAppState('calling')
  }

  const handleCallStart = () => {
    console.log('App: handleCallStart called, transitioning to in-call')
    // Add a smooth transition with fade out then slide in effect
    setAppState('in-call')
  }

  const handleCallEnd = () => {
    console.log('App: Call ended - transitioning to end screen')
    
    // Stop any playing audio immediately
    stopAllAudio()
    
    // Close the WebSocket connection
    if (backendWsRef.current && backendWsRef.current.readyState === WebSocket.OPEN) {
      console.log('App: Closing backend WebSocket connection')
      backendWsRef.current.close()
      backendWsRef.current = null
    }
    
    setAppState('ended')
  }

  const stopAllAudio = () => {
    console.log('App: Stopping all audio playback')
    
    // Stop all audio contexts
    if (typeof window !== 'undefined') {
      // Get all audio elements and pause them
      const audioElements = document.querySelectorAll('audio')
      audioElements.forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause()
          audio.currentTime = 0
        }
      })
      
      // Note: We can't directly access the audio contexts created by components
      // The components should handle their own cleanup
    }
  }

  const handleRestart = () => {
    // Restart with same role: create a fresh session and reset flags/audio
    if (!selectedRole) {
      setAppState('role-selection')
      return
    }

    // Stop any residual audio
    stopAllAudio()
    
    // Close existing WebSocket if any
    if (backendWsRef.current) {
      backendWsRef.current.close()
      backendWsRef.current = null
    }

    // Reset global flags
    ;(window as any).callEnded = false
    ;(window as any).hasPlayedGreetingAudio = false

    // Generate a fresh session id
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)

    // Persist fresh session
    localStorage.setItem('talkToZayn_sessionId', newSessionId)
    localStorage.setItem('talkToZayn_role', JSON.stringify(selectedRole))

    // Go to dialing to establish brand new sockets
    setAppState('calling')
  }

  const handleChangeRole = () => {
    // Clear session data and go back to role selection
    localStorage.removeItem('talkToZayn_sessionId')
    localStorage.removeItem('talkToZayn_role')
    
    // Close existing WebSocket if any
    if (backendWsRef.current) {
      backendWsRef.current.close()
      backendWsRef.current = null
    }
    
    // Reset global flags
    ;(window as any).callEnded = false
    ;(window as any).hasPlayedGreetingAudio = false
    setSessionId(null)
    setSelectedRole(null)
    setAppState('role-selection')
  }

  const handleKeepScrolling = () => {
    // Clear session data and go back to role selection
    localStorage.removeItem('talkToZayn_sessionId')
    localStorage.removeItem('talkToZayn_role')
    
    // Close existing WebSocket if any
    if (backendWsRef.current) {
      backendWsRef.current.close()
      backendWsRef.current = null
    }
    
    // Reset global flags
    ;(window as any).callEnded = false
    ;(window as any).hasPlayedGreetingAudio = false
    setSessionId(null)
    setSelectedRole(null)
    setAppState('role-selection')
  }

  console.log('App: Current state:', appState, 'Selected role:', selectedRole?.title)

  return (
    <div className="h-screen w-screen overflow-hidden">
      {appState === 'role-selection' && (
        <div className="animate-fadeIn">
          <RoleSelection onRoleSelect={handleRoleSelect} />
        </div>
      )}
      
      {appState === 'calling' && selectedRole && sessionId && (
        <div className="animate-fadeIn">
          <DialingScreen 
            role={selectedRole} 
            sessionId={sessionId}
            onCallStart={handleCallStart}
            onCallEnd={handleCallEnd}
            backendWsRef={backendWsRef}
          />
        </div>
      )}
      
      {appState === 'in-call' && selectedRole && sessionId && (
        <div className="animate-slideInFromRight">
          <CallInterface 
            role={selectedRole} 
            sessionId={sessionId}
            candidateName="there"
            onCallStart={handleCallStart}
            onCallEnd={handleCallEnd}
            backendWsRef={backendWsRef}
          />
        </div>
      )}
      
      {appState === 'ended' && selectedRole && (
        <div className="animate-fadeIn">
          <EndCallScreen 
            role={selectedRole}
            onRestart={handleRestart}
            onChangeRole={handleChangeRole}
            onKeepScrolling={handleKeepScrolling}
            isWebsiteMode={isWebsiteMode}
          />
        </div>
      )}
    </div>
  )
}

export default App
